import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Users, Search, Loader2, ShieldCheck, KeyRound,
  UserMinus, Sparkles, Crown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// iter132 Phase C — Admin User Lookup. Simple email search, results
// list. Click a row → in-place detail card with footprint data
// (reuses /admin/users/audit-footprint) plus quick-action links to
// the three existing action pages (Quick Upgrade, Rotate Password,
// Deactivate). Not a replacement for those pages — a hub.

export default function AdminUserLookup() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openEmail, setOpenEmail] = useState(null);

  const search = async () => {
    const query = q.trim();
    if (query.length < 2) return;
    setLoading(true);
    setResults([]);
    setOpenEmail(null);
    try {
      const { data } = await api.get(`/admin/users/lookup?q=${encodeURIComponent(query)}`);
      setResults(data.results || []);
      setTruncated(data.truncated || false);
      if (!data.results.length) toast("No users match this search");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-users-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors" data-testid="admin-users-back">
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </Link>

      <header>
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Admin</p>
        </div>
        <h1 className="font-heading text-3xl">User Lookup</h1>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Find a user by email (partial match). Click a row to see their footprint and jump to admin actions.
        </p>
      </header>

      <div className="flex gap-2">
        <Input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Type an email (min 2 chars)…"
          data-testid="admin-users-query"
          autoFocus
        />
        <Button onClick={search} disabled={q.trim().length < 2 || loading} data-testid="admin-users-search">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Search</>}
        </Button>
      </div>

      {results.length > 0 && (
        <>
          <p className="text-sm text-[var(--bt-text-muted)]" data-testid="admin-users-count">
            {results.length} match{results.length === 1 ? "" : "es"}{truncated ? " (truncated — refine your search)" : ""}
          </p>
          <ul className="space-y-2" data-testid="admin-users-list">
            {results.map((u) => (
              <ResultRow
                key={u.id}
                u={u}
                expanded={openEmail === u.email}
                onToggle={() => setOpenEmail(openEmail === u.email ? null : u.email)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ResultRow({ u, expanded, onToggle }) {
  const [footprint, setFootprint] = useState(null);
  const [loading, setLoading] = useState(false);
  const deactivated = !!u.deactivated_at;

  React.useEffect(() => {
    if (!expanded || footprint) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/audit-footprint?email=${encodeURIComponent(u.email)}`);
        if (!cancel) setFootprint(data);
      } catch (e) {
        if (!cancel) toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [expanded, u.email, footprint]);

  return (
    <li className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm" data-testid={`admin-users-row-${u.id}`}>
      <button onClick={onToggle} className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-[var(--bt-surface-hover)] transition-colors">
        <div className="min-w-0">
          <p className="font-medium truncate">{u.email}</p>
          <p className="text-xs text-[var(--bt-text-muted)] truncate">
            {u.name || "—"} · created {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {deactivated && <span className="text-xs px-2 py-0.5 border border-[var(--bt-loss)]/40 text-[var(--bt-loss)] rounded-sm">DEACTIVATED</span>}
          <span className="text-xs px-2 py-0.5 border border-[var(--bt-border)] rounded-sm uppercase font-mono">{u.role || "user"}</span>
          <span className="text-xs px-2 py-0.5 border border-[var(--bt-border)] rounded-sm uppercase">{u.subscription_tier || "free"}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--bt-border)] p-4 space-y-4">
          {loading && <p className="text-sm text-[var(--bt-text-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading footprint…</p>}
          {footprint && (
            <>
              <div>
                <p className="bt-label mb-1">Data footprint</p>
                {footprint.is_empty ? (
                  <p className="text-sm" style={{ color: "var(--bt-win)" }}>No user data.</p>
                ) : (
                  <p className="text-sm text-[var(--bt-text-muted)]">
                    <strong className="text-[var(--bt-text)]">{footprint.totals.data_rows}</strong> data rows across{" "}
                    {Object.entries(footprint.data_counts).filter(([_, n]) => n > 0).map(([k, n]) => `${k} (${n})`).join(", ")}
                  </p>
                )}
              </div>
              <div>
                <p className="bt-label mb-1">Audit references</p>
                <p className="text-sm text-[var(--bt-text-muted)]">{footprint.totals.audit_refs} row(s) preserved</p>
              </div>
              <div>
                <p className="bt-label mb-2">Quick actions</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/admin/quick-upgrade">
                    <Button variant="outline" size="sm" data-testid={`admin-users-action-upgrade-${u.id}`}>
                      <Crown className="w-3.5 h-3.5 mr-1.5" /> Change tier
                    </Button>
                  </Link>
                  <Link to="/admin/rotate-password">
                    <Button variant="outline" size="sm" data-testid={`admin-users-action-rotate-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Rotate password
                    </Button>
                  </Link>
                  <Link to="/admin/deactivate-user">
                    <Button variant="outline" size="sm" data-testid={`admin-users-action-deactivate-${u.id}`}>
                      <UserMinus className="w-3.5 h-3.5 mr-1.5" /> Deactivate
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-[var(--bt-text-muted)] mt-2">
                  Each action page has its own email confirmation flow — copy <span className="font-mono">{u.email}</span> when you get there.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}
